import React, { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import Icon from './Icon';
import { mediatorAPI, mediatorProductAPI } from '../../services/api';
import toast from 'react-hot-toast';
import PaginationBar from './PaginationBar';

const formatFeedbackLabel = (value) =>
  value
    ? value
        .toString()
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : '-';

const MediatorAssignmentModal = ({ isOpen, onClose, product }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mediators, setMediators] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({ page: 0, size: 5, totalElements: 0, totalPages: 0 });
  const [formData, setFormData] = useState({
    mediatorId: '',
    feedbackType: '',
    payoutType: 'LESS',
    payoutValue: '',
    totalSlots: '',
    remainingSlots: '',
    remainingTouched: false
  });
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null, name: '' });

  const feedbackConfigs = product?.feedbackConfigs || [];

  const availableMediators = useMemo(() => {
    if (!formData.feedbackType) return mediators;
    return mediators.filter(
      (mediator) =>
        String(mediator.id) === String(editingAssignment?.mediatorId) ||
        !assignments.some(
          (assignment) => String(assignment.mediatorId) === String(mediator.id) && assignment.feedbackType === formData.feedbackType
        )
    );
  }, [assignments, editingAssignment?.mediatorId, formData.feedbackType, mediators]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen || !product) return;
    if (product.restrictedToAssignedMediators === false) {
      toast('This product is handled through Available Deals, so manual assignment is disabled.', { icon: 'ℹ️' });
      onClose();
      return;
    }
    fetchAssignments();
    fetchMediators();
  }, [isOpen, product, pagination.page]);

  useEffect(() => {
    if (!showAddForm) {
      setFormData({ mediatorId: '', feedbackType: '', payoutType: 'LESS', payoutValue: '', totalSlots: '', remainingSlots: '', remainingTouched: false });
      setEditingAssignment(null);
    }
  }, [showAddForm]);

  const fetchAssignments = async () => {
    if (!product) return;
    setLoading(true);
    try {
      const response = await mediatorProductAPI.searchAssignments({
        productId: product.id,
        page: pagination.page,
        size: pagination.size,
        sortBy: 'createdBy',
        sortDirection: 'DESC'
      });
      const result = response.data.data;
      setAssignments(result.data || []);
      setPagination((prev) => ({ ...prev, totalElements: result.totalElements || 0, totalPages: result.totalPages || 0 }));
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      toast.error('Failed to load mediator assignments');
    } finally {
      setLoading(false);
    }
  };

  const fetchMediators = async () => {
    try {
      const response = await mediatorAPI.getAllMediators();
      const mediatorData = response.data.data || response.data || [];
      setMediators(mediatorData.filter((mediator) => mediator.active !== false));
    } catch (error) {
      console.error('Failed to fetch mediators:', error);
    }
  };

  const handleFeedbackTypeChange = (feedbackType) => {
    const matchingConfig = feedbackConfigs.find((config) => config.feedbackType === feedbackType);
    setFormData((prev) => ({
      ...prev,
      mediatorId: '',
      feedbackType,
      payoutType: matchingConfig?.payoutType || 'LESS',
      payoutValue: matchingConfig?.payoutValue ?? '',
      totalSlots: matchingConfig?.totalSlots ?? '',
      remainingSlots: matchingConfig?.remainingSlots ?? '',
      remainingTouched: false
    }));
  };

  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'remainingSlots') next.remainingTouched = true;
      if (field === 'totalSlots' && !prev.remainingTouched && (prev.remainingSlots === '' || prev.remainingSlots === prev.totalSlots)) {
        next.remainingSlots = value;
      }
      return next;
    });
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setShowAddForm(true);
    setFormData({
      mediatorId: assignment.mediatorId ? String(assignment.mediatorId) : '',
      feedbackType: assignment.feedbackType || '',
      payoutType: assignment.payoutType || 'LESS',
      payoutValue: assignment.payoutValue ?? assignment.lessAmount ?? '',
      totalSlots: assignment.totalSlots ?? '',
      remainingSlots: assignment.remainingSlots ?? '',
      remainingTouched: true
    });
  };

  const handleAddAssignment = async (event) => {
    event.preventDefault();

    if (!formData.mediatorId || !formData.feedbackType || formData.payoutValue === '' || formData.totalSlots === '' || formData.remainingSlots === '') {
      toast.error('Please fill mediator, feedback type, payout, and slot fields');
      return;
    }

    setSubmitting(true);
    try {
      await mediatorProductAPI.assignProduct({
        ...(editingAssignment?.id ? { id: editingAssignment.id } : {}),
        productId: product.id,
        mediatorId: formData.mediatorId,
        feedbackType: formData.feedbackType,
        payoutType: formData.payoutType,
        payoutValue: parseFloat(formData.payoutValue || 0),
        totalSlots: parseInt(formData.totalSlots, 10) || 0,
        remainingSlots: parseInt(formData.remainingSlots, 10) || 0
      });

      toast.success(editingAssignment ? 'Assignment updated successfully' : 'Mediator assigned successfully');
      setShowAddForm(false);
      fetchAssignments();
    } catch (error) {
      toast.error(error.response?.data?.message || (editingAssignment ? 'Failed to update assignment' : 'Failed to assign mediator'));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setSubmitting(true);
      await mediatorProductAPI.deleteAssignment(deleteConfirm.id);
      toast.success('Assignment removed');
      setDeleteConfirm({ show: false, id: null, name: '' });
      fetchAssignments();
    } catch (error) {
      toast.error('Failed to remove assignment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Assign Mediators - ${product?.productName || ''}`} width={860}>
      <div style={{ padding: '24px', position: 'relative' }}>
        <div style={{ padding: '14px 16px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>Restricted assignment mode</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>Only assigned mediators with remaining slots can submit this deal. Payout and slots are stored per feedback type.</div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Active Assignments</h4>
          <Button size="sm" variant={showAddForm ? 'secondary' : 'primary'} onClick={() => setShowAddForm((value) => !value)}>
            <Icon name={showAddForm ? 'x' : 'plus'} size={16} style={{ marginRight: '8px' }} />
            {showAddForm ? 'Cancel' : 'Assign New'}
          </Button>
        </div>

        {showAddForm && (
          <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
            {editingAssignment && (
              <div style={{ marginBottom: '14px', padding: '10px 12px', borderRadius: '10px', background: '#eef2ff', border: '1px solid #c7d2fe', fontSize: '13px', color: '#4338ca', fontWeight: '600' }}>
                Editing assignment values for {formatFeedbackLabel(editingAssignment.feedbackType)}. Mediator and feedback type stay locked to avoid duplicate rows.
              </div>
            )}
            <form onSubmit={handleAddAssignment} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Feedback Type</label>
                <select value={formData.feedbackType} onChange={(event) => handleFeedbackTypeChange(event.target.value)} disabled={Boolean(editingAssignment)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', opacity: editingAssignment ? 0.7 : 1 }}>
                  <option value="">Select feedback type...</option>
                  {feedbackConfigs.map((config) => <option key={config.id || config.feedbackType} value={config.feedbackType}>{formatFeedbackLabel(config.feedbackType)}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Mediator</label>
                <select value={formData.mediatorId} onChange={(event) => handleFieldChange('mediatorId', event.target.value)} disabled={!formData.feedbackType || Boolean(editingAssignment)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px', opacity: !formData.feedbackType || editingAssignment ? 0.65 : 1 }}>
                  <option value="">{formData.feedbackType ? 'Select mediator...' : 'Pick feedback type first'}</option>
                  {availableMediators.map((mediator) => <option key={mediator.id} value={mediator.id}>{mediator.mediatorName || mediator.fullName || mediator.username || mediator.mediatorCode}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Payout Type</label>
                <select value={formData.payoutType} onChange={(event) => handleFieldChange('payoutType', event.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}>
                  <option value="LESS">LESS</option>
                  <option value="COMMISSION">COMMISSION</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Payout Value</label>
                <input type="number" step="0.01" value={formData.payoutValue} onChange={(event) => handleFieldChange('payoutValue', event.target.value)} placeholder="0.00" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Total Slots</label>
                <input type="number" min="0" value={formData.totalSlots} onChange={(event) => handleFieldChange('totalSlots', event.target.value)} placeholder="0" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>Remaining Slots</label>
                <input type="number" min="0" value={formData.remainingSlots} onChange={(event) => handleFieldChange('remainingSlots', event.target.value)} placeholder="0" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }} />
              </div>
              <Button type="submit" loading={submitting} style={{ height: '42px' }}>{editingAssignment ? 'Update' : 'Save'}</Button>
            </form>
          </div>
        )}

        <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <tr>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#64748b' }}>Mediator</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#64748b' }}>Feedback Type</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#64748b' }}>Payout Type</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#64748b' }}>Payout Value</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#64748b' }}>Slots</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#64748b' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>Loading assignments...</td></tr>
              ) : assignments.length === 0 ? (
                <tr><td colSpan="6" style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>No mediators assigned to this product yet.</td></tr>
              ) : (
                assignments.map((assignment) => {
                  const mediator = mediators.find((item) => String(item.id) === String(assignment.mediatorId));
                  const displayName = assignment.mediatorName || mediator?.mediatorName || mediator?.fullName || mediator?.username || mediator?.mediatorCode || 'Unknown Mediator';
                  return (
                    <tr key={assignment.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 16px', fontWeight: '500', color: '#1e293b' }}>{displayName}</td>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>{formatFeedbackLabel(assignment.feedbackType)}</td>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>{assignment.payoutType || 'LESS'}</td>
                      <td style={{ padding: '12px 16px', color: '#059669', fontWeight: '600' }}>₹{assignment.payoutValue ?? assignment.lessAmount}</td>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>{assignment.remainingSlots ?? 0}/{assignment.totalSlots ?? 0}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          <button onClick={() => handleEditAssignment(assignment)} style={{ background: '#eff6ff', border: 'none', color: '#2563eb', cursor: 'pointer', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Icon name="req" size={14} />
                            Edit
                          </button>
                          <button onClick={() => setDeleteConfirm({ show: true, id: assignment.id, name: `${displayName} (${formatFeedbackLabel(assignment.feedbackType)})` })} style={{ background: '#fee2e2', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <Icon name="trash-2" size={14} />
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
                    {pagination.totalPages > 1 && (
            <PaginationBar
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalElements={pagination.totalElements}
              pageSize={pagination.size}
              onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
              style={{ marginTop: '12px', justifyContent: 'center' }}
            />
          )}
        </div>

        {deleteConfirm.show && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, borderRadius: '12px' }}>
            <div style={{ textAlign: 'center', padding: '24px', maxWidth: '320px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto' }}><Icon name="alert-circle" size={24} /></div>
              <h5 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700' }}>Confirm Removal</h5>
              <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#64748b', lineHeight: '1.5' }}>Remove the assignment for <strong>{deleteConfirm.name}</strong>?</p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm({ show: false, id: null, name: '' })} style={{ flex: 1 }}>Cancel</Button>
                <Button size="sm" onClick={confirmDelete} loading={submitting} style={{ flex: 1, background: '#ef4444' }}>Remove</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default MediatorAssignmentModal;
